<!-- Popup: TOC(Table of Contents) -->
<script lang="ts" setup>
import { computed, inject, nextTick, ref, watch } from 'vue'
import { useScroll } from '@vueuse/core'
import { api } from '@/api'
import { useLoadingStore } from '@/stores'
import { CHAPTER_LOCK } from '@/constants'
import { useElementClick } from '@/composables/tracks'
import { useI18n } from '@/composables/useI18n'
import { formatUpdateTime } from '@/utils'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

const props = withDefaults(
  defineProps<{
    value: boolean
    book: any
    contentId: string
    chapterId?: string
    checkPassGotoChapter?: (index: number) => boolean
    lockIconColor?: string // 锁图标颜色,由父组件传入
    routeName?: string // 跳转路由名称，默认为 'chapter'
  }>(), {
    checkPassGotoChapter: () => true,
    routeName: READER_ROUTE_NAME,
  },
)

defineEmits<{
  (event: 'input', value: boolean): void
}>()

const LONG_BOOK_CHAPTER_THRESHOLD = 25

const theme = inject<{ background: string } | null>('theme', { background: 'white' })
const { $showLoading, $hideLoading } = useLoadingStore()
const { elementClick } = useElementClick()
const { t } = useI18n()

const chapterList = ref<GetContentGetcontentchapterbooklistResponse['data']['records']>([])
const chapterScrollRef = ref()
const { y: chapterScrollY } = useScroll(chapterScrollRef, { behavior: 'smooth' })
const chooseChapterOpitons = ref(false)
const chapterOptions = ref<any[]>([])
const chapterTotal = ref<number>(0)
const pagination = ref({
  pageIndex: 1,
  pageSize: 100,
})
const showUpdateTime = computed(() => chapterTotal.value > LONG_BOOK_CHAPTER_THRESHOLD)

async function fetchChapterList() {
  $showLoading()
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', { params: { contentId: props.contentId, ...pagination.value } })
    chapterList.value = chapterListData.records
    chapterTotal.value = chapterListData.total
    const page = chapterListData.pages
    // Note: 章节分页 options
    chapterOptions.value = Array.from({ length: page })
      .map((_, index) => ({
        label: `${(index * pagination.value.pageSize + 1)} - ${(index + 1) * pagination.value.pageSize}`,
        value: index + 1,
      }))
    // Note: auto scroll to current chapterId
    if (props.chapterId) {
      const itemHeight = showUpdateTime.value ? 65 : 57
      const index = chapterList.value.findIndex(item => item.id === props.chapterId)
      nextTick(() => {
        if (index > -1)
          chapterScrollY.value = index * itemHeight
      })
    }
  }
  finally {
    $hideLoading()
  }
}
async function onOpen() {
  await fetchChapterList()
}

function selectChapter(item: any) {
  pagination.value.pageIndex = item.value
}

function toChapter($event: Event, item: any, navigate: any) {
  if (props.checkPassGotoChapter(item.sequenceNo))
    navigate($event)
}

watch(() => pagination.value.pageIndex, async () => {
  await fetchChapterList()
  chooseChapterOpitons.value = false
})

watch(
  () => props.value,
  (val) => {
    if (val)
      elementClick('catalogue')
  },
)
</script>

<template>
  <!-- Popup: 查看章节 -->
  <BasicPopup :value="value" @input="value => $emit('input', value)" @open="onOpen">
    <div v-if="book" class="max-h-90dvh overflow-scroll rounded-t" :style="`background: ${theme ? theme.background : ''}`">
      <div class="pt-5 pb-13px px-5 bg-b-light">
        <div class="flex space-x-10px">
          <img class="w-55px h-77px rounded-3px" :src="book.contentCoverUrl">
          <div class="flex flex-col justify-center">
            <p class="text-17px font-bold line-clamp-2">
              {{ book.contentName }}
            </p>
            <p class="mt-2 text-13px text-n-text-3">
              {{ chapterTotal }} {{ t('episodes') }}
            </p>
          </div>
        </div>
      </div>
      <ul v-show="chooseChapterOpitons" class="px-5 h-[calc(90dvh-150px)] overflow-scroll">
        <li
          v-for="item in chapterOptions" :key="item.value"
          class="toc-item h-57px lh-57px flex items-center justify-between"
          @click="selectChapter(item)"
        >
          <span :class="{ 'text-n-primary! font-bold': pagination.pageIndex === item.value }">{{ item.label }}</span>
          <i-assets-icon-check v-if="pagination.pageIndex === item.value" class="w-17px" />
        </li>
      </ul>
      <ul v-show="!chooseChapterOpitons" ref="chapterScrollRef" class="px-5 h-[calc(90dvh-150px)] overflow-scroll">
        <router-link
          v-for="item in chapterList" :key="item.id"
          v-slot="{ navigate }" custom
          :to="{ name: routeName, params: { contentId, chapterId: item.id }, query: { skipPosition: '1' } }" replace
        >
          <li
            class="toc-item flex items-center justify-between"
            :class="showUpdateTime ? 'h-65px' : 'h-57px lh-57px'"
            @click="toChapter($event, item, navigate)"
          >
            <div v-if="showUpdateTime" class="flex-1 min-w-0">
              <div class="text-truncate" :class="{ 'text-n-primary! font-bold': chapterId === item.id }">
                {{ item.chapterName }}
              </div>
              <div class="mt-2 text-11px lh-12px text-n-text-3">
                {{ formatUpdateTime(item.updateDateTime) }}
              </div>
            </div>
            <span v-else class="flex-1 text-truncate" :class="{ 'text-n-primary! font-bold': chapterId === item.id }">{{ item.chapterName }}</span>
            <i-assets-icon-lock
              v-if="item.lockStatus === CHAPTER_LOCK.LOCK"
              class="w-17px toc-lock-icon"
              :style="lockIconColor ? { color: lockIconColor } : undefined"
            />
          </li>
        </router-link>
      </ul>
    </div>
  </BasicPopup>
</template>

<style scoped>
.toc-lock-icon {
  /* 默认使用 CSS 变量,如果没有则使用默认黑色 */
  color: var(--toc-lock-color, rgba(0, 0, 0, 0.3));
}

.toc-item {
  /* 章节分隔线 - 使用主题配置的颜色 */
  border-bottom: 1px solid var(--toc-border-color, #ECEDEF);
}
</style>
